import { commentTable, reactionTable, readSyncMeta, writeSyncMeta } from './entityCache';
import { getEffectiveTenantHost } from '../config';
import { adoptLegacyDatabase } from './legacyCleanup';
import { closeLegacyHazardDatabase, openLegacyHazardDatabase } from './legacyReader';
import type { Comment, Hazard } from './types';
import { applyServerRows, clearCache, markViewed as cacheMarkViewed, noteDroppedPhotoIds, photoIdsDropped, removeHazards, replaceRange, writeDisplayReaction, type RangeReplacePlan } from './cacheStore';
import { dataUrlToFile } from '../services/syncUtils';
import eventEmitter, { EVENTS } from '../services/eventService';
import {
  enqueueHazardOp,
  findHazardOp,
  hasPendingOutbox,
  listHazardOps,
} from './outbox';
import {
  deleteUserStore,
  getOpenUserStore,
  openUserStore,
  readCachedTenantId,
  readOfflineIdentity,
  type HazardOpKind,
  type OutboxOp,
  type UserStore,
} from './store';
import { backoffMs, isBlockedByDependency } from './outboxPolicy';
import type { OpKind } from './store';
import { compareFeedOrder, idKey, toEpochMs } from './time';
export type { OutboxOp } from './store';

const MAX_ATTEMPTS = 8;

function notifyCache(): void {
  eventEmitter.emit(EVENTS.CACHE_CHANGED);
}

function hazardRecord(hazard: Hazard): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(hazard)) {
    record[key] = value;
  }
  return record;
}

async function requireStore(): Promise<UserStore> {
  const open = getOpenUserStore();
  if (open) {
    return open;
  }
  const identity = readSessionUser();
  if (!identity) {
    throw new Error('User store is not open');
  }
  return openUserStore({
    host: identity.host,
    userId: identity.userId,
    tenantId: readCachedTenantId(identity.userId),
  });
}

function readSessionUser(): { host: string; userId: string } | null {
  if (typeof localStorage === 'undefined' || typeof window === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem('oidc_user_info');
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { userId?: unknown };
    if (typeof parsed.userId !== 'string' || parsed.userId.length === 0) {
      return null;
    }
    return { host: getEffectiveTenantHost() || window.location.host, userId: parsed.userId };
  } catch {
    return null;
  }
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string');
}

function toHazard(row: Record<string, unknown>): Hazard {
  const hazard: Hazard = {
    id: String(row.id ?? ''),
    description: String(row.description ?? ''),
    image_data: stringArray(row.image_data),
    utc_timestamp: String(row.utc_timestamp ?? ''),
    latitude: Number(row.latitude ?? 0),
    longitude: Number(row.longitude ?? 0),
  };
  const skip = new Set([
    'id',
    'idKey',
    'sortTs',
    'inWindow',
    'viewedAt',
    'description',
    'image_data',
    'utc_timestamp',
    'latitude',
    'longitude',
  ]);
  const extra: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!skip.has(key) && value !== undefined) {
      extra[key] = value;
    }
  }
  Object.assign(hazard, extra);
  return hazard;
}

async function displayRefs(store: UserStore, refs: string[]): Promise<string[]> {
  const out: string[] = [];
  for (const ref of refs) {
    if (!ref.startsWith('media:')) {
      out.push(ref);
      continue;
    }
    const media = await store.outboxMedia.get(ref.slice('media:'.length));
    if (!media) {
      out.push(ref);
      continue;
    }
    out.push(await blobToDataUrl(media.blob));
  }
  return out;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Failed to read media blob'));
    reader.readAsDataURL(blob);
  });
}

async function hazardFromOp(store: UserStore, op: OutboxOp): Promise<Hazard> {
  const payload: Record<string, unknown> = { ...op.payload, id: op.entityId };
  if (op.kind === 'hazard.delete') {
    payload.deleted = true;
  }
  const refs = stringArray(payload.image_data);
  if (refs.some((ref) => ref.startsWith('media:'))) {
    payload.image_data = await displayRefs(store, refs);
  }
  return toHazard(payload);
}

/**
 * A resolve/update op often carries only the changed fields. `toHazard` fills
 * the rest with blanks, and spreading that over the cached row drops the
 * timestamp, description, and media so the card leaves the feed until the
 * next full reload. Copy only keys the op actually set.
 */
function overlayOpOnCached(existing: Hazard, op: OutboxOp, local: Hazard): Hazard {
  const next: Hazard = {
    ...existing,
    id: op.entityId,
    outbox_status: op.status === 'dead' ? 'failed' : 'pending',
  };
  const localRecord = local as unknown as Record<string, unknown>;
  const nextRecord = next as unknown as Record<string, unknown>;
  for (const key of Object.keys(op.payload)) {
    if (key === 'id' || op.payload[key] === undefined) continue;
    if (key === 'image_data') {
      next.image_data = local.image_data;
      continue;
    }
    nextRecord[key] = localRecord[key];
  }
  return next;
}

/** Cache rows in feed order with pending outbox ops overlaid. Pending deletes are hidden. */
export async function list(): Promise<Hazard[]> {
  const store = await requireStore();
  const cached = await store.hazards.orderBy('[sortTs+idKey]').reverse().toArray();
  const byId = new Map<string, Hazard>();
  for (const row of cached) {
    byId.set(row.id, toHazard(row));
  }
  const ops = await listHazardOps(store);
  const hidden = new Set<string>();
  const created: Hazard[] = [];
  for (const op of ops) {
    if (!op.kind.startsWith('hazard.')) {
      continue;
    }
    if (op.kind === 'hazard.delete') {
      hidden.add(op.entityId);
      continue;
    }
    const local = await hazardFromOp(store, op);
    const existing = byId.get(op.entityId);
    if (op.kind === 'hazard.create' && !existing) {
      created.push({ ...local, outbox_status: op.status === 'dead' ? 'failed' : 'pending' });
      continue;
    }
    if (existing) {
      byId.set(op.entityId, overlayOpOnCached(existing, op, local));
    } else if (op.kind === 'hazard.update' || op.kind === 'hazard.resolve') {
      created.push({ ...local, outbox_status: op.status === 'dead' ? 'failed' : 'pending' });
    }
  }
  const visible = Array.from(byId.values()).filter((row) => !hidden.has(row.id) && row.deleted !== true);
  const merged = [...visible, ...created.filter((row) => !hidden.has(row.id))];
  merged.sort((a, b) =>
    compareFeedOrder(
      { sortTs: toEpochMs(a.utc_timestamp) ?? 0, idKey: idKey(a.id) },
      { sortTs: toEpochMs(b.utc_timestamp) ?? 0, idKey: idKey(b.id) }
    )
  );
  return merged;
}

export async function get(id: string): Promise<Hazard | undefined> {
  const rows = await list();
  return rows.find((row) => row.id === id);
}

export async function feedPage(
  page: number,
  pageSize: number
): Promise<{ hazards: Hazard[]; hasMore: boolean; totalCount: number }> {
  const rows = await list();
  const start = Math.max(0, (page - 1) * pageSize);
  const hazards = rows.slice(start, start + pageSize);
  return {
    hazards,
    hasMore: start + hazards.length < rows.length,
    totalCount: rows.length,
  };
}

export async function feedSlice(offset: number, limit: number): Promise<Hazard[]> {
  const rows = await list();
  return rows.slice(offset, offset + limit);
}

export async function addLocal(
  hazard: Hazard,
  options?: { files?: File[]; customTags?: string[] }
): Promise<void> {
  const store = await requireStore();
  const existing = await findHazardOp(store, hazard.id);
  if (existing) {
    return;
  }
  const media: Array<{ id: string; filename: string; mimeType: string; blob: Blob }> = [];
  const imageData: string[] = [];
  const files = options?.files ?? [];
  if (files.length > 0) {
    for (const file of files) {
      const mediaId = crypto.randomUUID();
      media.push({ id: mediaId, filename: file.name || 'upload', mimeType: file.type || 'application/octet-stream', blob: file });
      imageData.push(`media:${mediaId}`);
    }
  } else {
    for (let index = 0; index < (hazard.image_data ?? []).length; index += 1) {
      const ref = hazard.image_data[index] ?? '';
      if (ref.startsWith('data:')) {
        const file = dataUrlToFile(ref, `hazard-media-${index}`);
        const mediaId = crypto.randomUUID();
        media.push({ id: mediaId, filename: file.name, mimeType: file.type, blob: file });
        imageData.push(`media:${mediaId}`);
      } else {
        imageData.push(ref);
      }
    }
  }
  const payload: Record<string, unknown> = { ...hazard, image_data: imageData };
  if (options?.customTags && options.customTags.length > 0) {
    payload.custom_tags = options.customTags;
  }
  await enqueueHazardOp(store, {
    kind: 'hazard.create',
    entityId: hazard.id,
    payload,
    media,
  });
  notifyCache();
}

export async function queueDelete(id: string): Promise<void> {
  const store = await requireStore();
  const existing = await findHazardOp(store, id);
  if (existing?.kind === 'hazard.create') {
    await store.outbox.delete(existing.id);
    await store.outboxMedia.where('opId').equals(existing.id).delete();
    notifyCache();
    return;
  }
  if (existing) {
    return;
  }
  await enqueueHazardOp(store, {
    kind: 'hazard.delete',
    entityId: id,
    payload: { id },
  });
  notifyCache();
}

export async function queueUpdate(id: string, patch: Record<string, unknown>): Promise<void> {
  await mergeOrEnqueue(id, 'hazard.update', patch);
}

export async function queueResolve(id: string, resolved: boolean): Promise<void> {
  await mergeOrEnqueue(id, 'hazard.resolve', { id, resolved });
}

async function mergeOrEnqueue(
  id: string,
  kind: HazardOpKind,
  patch: Record<string, unknown>
): Promise<void> {
  const store = await requireStore();
  const existing = await findHazardOp(store, id);
  if (existing && (existing.kind === 'hazard.create' || existing.kind === kind)) {
    await store.outbox.update(existing.id, {
      payload: { ...existing.payload, ...patch, id },
    });
    notifyCache();
    return;
  }
  if (existing) {
    return;
  }
  await enqueueHazardOp(store, { kind, entityId: id, payload: { ...patch, id } });
  notifyCache();
}

/** Photo ids a hazard_updated event removed. Recorded before the feed prefix refresh. */
export function noteDroppedPhotoIdsFromMedia(
  hazardId: string,
  previousMedia: unknown,
  incomingMedia: unknown,
  incomingImages?: unknown,
  previousImages?: unknown
): void {
  const store = getOpenUserStore();
  if (!store) {
    return;
  }
  noteDroppedPhotoIds(
    store,
    hazardId,
    photoIdsDropped(previousMedia, incomingMedia, incomingImages, previousImages)
  );
}

export async function put(hazard: Hazard): Promise<void> {
  if (hazard.deleted) {
    await remove(hazard.id);
    return;
  }
  const store = await requireStore();
  await applyServerRows(store, { hazards: [hazardRecord(hazard)] });
  const op = await findHazardOp(store, hazard.id);
  if (op && hazard.last_synced) {
    await finishOp(store, op);
  }
}

export async function bulkPut(hazards: Hazard[]): Promise<void> {
  const store = await requireStore();
  const live = hazards.filter((row) => row.deleted !== true);
  const gone = hazards.filter((row) => row.deleted === true).map((row) => row.id);
  if (gone.length > 0) {
    await removeHazards(store, gone);
  }
  if (live.length > 0) {
    await applyServerRows(store, {
      hazards: live.map((row) => hazardRecord(row)),
    });
  }
}

export async function update(id: string, patch: Partial<Hazard>): Promise<void> {
  const store = await requireStore();
  const op = await findHazardOp(store, id);
  const synced = typeof patch.last_synced === 'string' && patch.last_synced.length > 0;
  const terminal = patch.sync_dead === true;
  if (op) {
    if (terminal) {
      await store.outbox.update(op.id, { status: 'dead' });
      return;
    }
    if (synced) {
      if (op.kind === 'hazard.delete' || patch.deleted === true) {
        await removeHazards(store, [id]);
        await finishOp(store, op);
        return;
      }
      await applyServerRows(store, {
        hazards: [{ ...op.payload, ...patch, id }],
      });
      await finishOp(store, op);
      return;
    }
    if (patch.deleted === true) {
      await queueDelete(id);
      return;
    }
    const next = { ...op.payload };
    for (const [key, value] of Object.entries(patch)) {
      if (key === 'sync_attempts' || key === 'sync_error' || key === 'sync_dead' || key === 'last_synced') {
        continue;
      }
      next[key] = value;
    }
    const attempts = typeof patch.sync_attempts === 'number' ? patch.sync_attempts : op.attempts;
    await store.outbox.update(op.id, { payload: next, attempts });
    notifyCache();
    return;
  }
  if (patch.deleted === true && !synced) {
    await queueDelete(id);
    return;
  }
  if (patch.deleted === true && synced) {
    await removeHazards(store, [id]);
    return;
  }
  const existing = await store.hazards.get(id);
  if (!existing) {
    return;
  }
  await applyServerRows(store, {
    hazards: [{ ...existing, ...patch, id }],
  });
}

export async function dropCached(id: string): Promise<void> {
  const store = await requireStore();
  await removeHazards(store, [id]);
}

export function hasOpenStore(): boolean {
  return getOpenUserStore() !== null;
}

export function sessionUserId(): string {
  return readOfflineIdentity()?.userId ?? '';
}

export async function cachedFeedRows(): Promise<Array<{
  id: string;
  sortTs: number;
  idKey: string;
  viewedAt?: number;
  user_id?: string;
}>> {
  const store = getOpenUserStore();
  if (!store) {
    return [];
  }
  const rows = await store.hazards.toArray();
  return rows.map((row) => ({
    id: row.id,
    sortTs: row.sortTs,
    idKey: row.idKey,
    viewedAt: row.viewedAt,
    user_id: typeof row.user_id === 'string' ? row.user_id : undefined,
  }));
}

export async function applyFeedPlan(plan: RangeReplacePlan): Promise<void> {
  const store = await requireStore();
  await replaceRange(store, plan);
}

export async function applyServerComments(rows: Comment[]): Promise<void> {
  const store = await requireStore();
  const plain = rows.map((row) => {
    const copy: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      copy[key] = value;
    }
    return copy;
  });
  await applyServerRows(store, { comments: plain });
}

export async function applyFeedReactions(rows: Array<Record<string, unknown>>): Promise<void> {
  const store = await requireStore();
  await applyServerRows(store, { reactions: rows });
}

export async function markViewed(id: string): Promise<void> {
  const store = getOpenUserStore();
  if (!store) {
    return;
  }
  await cacheMarkViewed(store, id);
}

export async function remove(id: string): Promise<void> {
  const store = await requireStore();
  await removeHazards(store, [id]);
  const op = await findHazardOp(store, id);
  if (op?.kind === 'hazard.delete') {
    await finishOp(store, op);
  }
}

export async function clearSynced(): Promise<void> {
  const store = await requireStore();
  await clearCache(store);
}

export async function recordFailure(
  id: string,
  options: { syncError?: string; terminal?: boolean; pause?: boolean } = {}
): Promise<void> {
  const store = await requireStore();
  const op = await findHazardOp(store, id);
  if (!op) {
    return;
  }
  if (options.pause === true) {
    await store.outbox.update(op.id, {
      nextAttemptAt: Date.now() + backoffMs(op.attempts),
      payload: options.syncError ? { ...op.payload, sync_error: options.syncError } : op.payload,
    });
    return;
  }
  const attempts = op.attempts + 1;
  const dead = options.terminal === true || attempts > MAX_ATTEMPTS;
  await store.outbox.update(op.id, {
    attempts,
    nextAttemptAt: dead ? op.nextAttemptAt : Date.now() + backoffMs(attempts),
    status: dead ? 'dead' : 'pending',
    payload: options.syncError ? { ...op.payload, sync_error: options.syncError } : op.payload,
  });
}

async function finishOp(store: UserStore, op: OutboxOp): Promise<void> {
  await store.outboxMedia.where('opId').equals(op.id).delete();
  await store.outbox.delete(op.id);
  // applyServerRows notifies before this delete, so the feed would keep the
  // pending overlay unless a second change event runs after the op is gone.
  notifyCache();
}

function isHazardKind(kind: OpKind): kind is HazardOpKind {
  return kind === 'hazard.create'
    || kind === 'hazard.update'
    || kind === 'hazard.delete'
    || kind === 'hazard.resolve';
}

export async function hasPending(): Promise<boolean> {
  const open = getOpenUserStore();
  if (!open) {
    return false;
  }
  return hasPendingOutbox(open);
}

export async function listPendingHazards(): Promise<Array<{ kind: HazardOpKind; hazard: Hazard }>> {
  const store = await requireStore();
  const ops = await listHazardOps(store);
  const byId = new Map(ops.map((op) => [op.id, op]));
  const pending = ops.filter((op): op is OutboxOp & { kind: HazardOpKind } => {
    if (op.status !== 'pending' || !isHazardKind(op.kind)) {
      return false;
    }
    if (op.nextAttemptAt > Date.now()) {
      return false;
    }
    return !isBlockedByDependency(op, byId);
  });
  const rows = [];
  for (const op of pending) {
    rows.push({ kind: op.kind, hazard: await hazardFromOp(store, op) });
  }
  return rows;
}

export async function deleteOpenUserStore(): Promise<void> {
  await deleteUserStore();
}

export async function findOpByEntity(entityId: string): Promise<OutboxOp | undefined> {
  const store = getOpenUserStore();
  if (!store) {
    return undefined;
  }
  return findHazardOp(store, entityId);
}

export async function listOutboxOps(): Promise<OutboxOp[]> {
  const store = getOpenUserStore();
  if (!store) {
    return [];
  }
  return listHazardOps(store);
}

export async function retryOutboxOp(opId: string): Promise<void> {
  const store = await requireStore();
  await store.outbox.update(opId, {
    status: 'pending',
    attempts: 0,
    nextAttemptAt: Date.now(),
  });
}

export async function discardOutboxOp(opId: string): Promise<void> {
  const store = await requireStore();
  await store.outboxMedia.where('opId').equals(opId).delete();
  await store.outbox.delete(opId);
  notifyCache();
}

export async function resolveMediaRefs(refs: string[]): Promise<string[]> {
  const store = getOpenUserStore();
  if (!store) {
    return refs;
  }
  return displayRefs(store, refs);
}

export async function dueOps(): Promise<OutboxOp[]> {
  const store = getOpenUserStore();
  if (!store) {
    return [];
  }
  const ops = await listHazardOps(store);
  const byId = new Map(ops.map((op) => [op.id, op]));
  const now = Date.now();
  return ops.filter((op) => {
    if (op.status !== 'pending' || op.nextAttemptAt > now) {
      return false;
    }
    return !isBlockedByDependency(op, byId);
  });
}

export async function opMedia(opId: string): Promise<Array<{ id: string; blob: Blob; filename: string; mimeType: string }>> {
  const store = getOpenUserStore();
  if (!store) {
    return [];
  }
  const rows = await store.outboxMedia.where('opId').equals(opId).toArray();
  return rows.map((row) => ({
    id: row.id,
    blob: row.blob,
    filename: row.filename,
    mimeType: row.mimeType,
  }));
}

export async function completeOp(opId: string, payloadPatch?: Record<string, unknown>): Promise<void> {
  const store = await requireStore();
  const op = await store.outbox.get(opId);
  if (!op) {
    return;
  }
  if (payloadPatch) {
    await store.outbox.update(opId, { payload: { ...op.payload, ...payloadPatch } });
  }
  await finishOp(store, op);
  notifyCache();
}

export async function applyOpResult(
  opId: string,
  disposition: 'retry' | 'pause' | 'dead',
  error?: string
): Promise<void> {
  const store = await requireStore();
  const op = await store.outbox.get(opId);
  if (!op) {
    return;
  }
  if (disposition === 'pause') {
    await store.outbox.update(opId, { nextAttemptAt: Date.now() + backoffMs(op.attempts) });
    return;
  }
  const attempts = op.attempts + 1;
  const dead = disposition === 'dead';
  await store.outbox.update(opId, {
    attempts,
    status: dead ? 'dead' : 'pending',
    nextAttemptAt: dead ? op.nextAttemptAt : Date.now() + backoffMs(attempts),
    payload: error ? { ...op.payload, sync_error: error } : op.payload,
  });
}

export async function enqueueUserOp(input: {
  kind: OpKind;
  entityId: string;
  payload: Record<string, unknown>;
  dependsOn?: string;
  files?: File[];
}): Promise<string> {
  const store = await requireStore();
  const existing = await findHazardOp(store, input.entityId);
  if (existing && (existing.kind === input.kind || existing.kind === 'comment.create' || existing.kind === 'hazard.create')) {
    const media = filesToMedia(input.files);
    await store.outbox.update(existing.id, {
      payload: { ...existing.payload, ...input.payload },
      dependsOn: input.dependsOn ?? existing.dependsOn,
    });
    for (const item of media) {
      await store.outboxMedia.add({ ...item, opId: existing.id });
    }
    notifyCache();
    return existing.id;
  }
  const media = filesToMedia(input.files);
  const imageRefs = media.map((item) => `media:${item.id}`);
  const payload = imageRefs.length > 0
    ? { ...input.payload, image_data: [...stringArray(input.payload.image_data).filter((ref) => !ref.startsWith('media:')), ...imageRefs] }
    : input.payload;
  const id = await enqueueHazardOp(store, {
    kind: input.kind,
    entityId: input.entityId,
    payload,
    dependsOn: input.dependsOn,
    media,
  });
  notifyCache();
  return id;
}

function filesToMedia(files: File[] | undefined): Array<{ id: string; filename: string; mimeType: string; blob: Blob }> {
  return (files ?? []).map((file) => ({
    id: crypto.randomUUID(),
    filename: file.name || 'upload',
    mimeType: file.type || 'application/octet-stream',
    blob: file,
  }));
}

export async function dropOpForEntity(entityId: string): Promise<void> {
  const store = await requireStore();
  const existing = await findHazardOp(store, entityId);
  if (!existing) {
    return;
  }
  await store.outboxMedia.where('opId').equals(existing.id).delete();
  await store.outbox.delete(existing.id);
  notifyCache();
}

export async function promoteLegacyQueues(): Promise<void> {
  const store = getOpenUserStore();
  if (!store) {
    return;
  }
  const pendingComments = await store.comments.filter((row) => row.legacyPending === 1).toArray();
  for (const comment of pendingComments) {
    const existing = await findHazardOp(store, comment.id);
    if (existing) {
      continue;
    }
    const hazardOp = await findHazardOp(store, comment.incident_id);
    const deleted = comment.deleted === true;
    await enqueueHazardOp(store, {
      kind: deleted ? 'comment.delete' : 'comment.create',
      entityId: comment.id,
      payload: { ...comment },
      dependsOn: !deleted && hazardOp?.kind === 'hazard.create' ? hazardOp.id : undefined,
    });
  }
  const copied = await store.legacySyncQueue.toArray();
  for (const item of copied) {
    await enqueueReactionFromLegacy(store, item.reaction_id, item.operation, item.content_type, item.content_id, item.attempts);
    if (item.localId != null) {
      await store.legacySyncQueue.delete(item.localId);
    }
  }
  const legacy = await openLegacyHazardDatabase();
  if (!legacy) {
    return;
  }
  try {
    const live = await legacy.syncQueue.toArray();
    for (const item of live) {
      if (item.migrated === true) {
        if (item.id != null) {
          await legacy.syncQueue.delete(item.id);
        }
        continue;
      }
      const reaction = await legacy.reactions.get(item.reaction_id);
      if (reaction && !(await store.reactions.get(reaction.id))) {
        const record: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(reaction)) {
          if (value !== undefined) {
            record[key] = value;
          }
        }
        await writeDisplayReaction(store, record);
      }
      await enqueueReactionFromLegacy(
        store,
        item.reaction_id,
        item.operation,
        item.content_type,
        item.content_id,
        item.attempts
      );
      if (item.id != null) {
        await legacy.syncQueue.delete(item.id);
      }
    }
  } finally {
    await closeLegacyHazardDatabase(legacy);
  }
}

async function enqueueReactionFromLegacy(
  store: UserStore,
  reactionId: string,
  operation: 'CREATE' | 'DELETE',
  contentType: 'card' | 'comment',
  contentId: string,
  attempts: number
): Promise<void> {
  const existing = await findHazardOp(store, reactionId);
  if (existing) {
    return;
  }
  const reaction = await store.reactions.get(reactionId);
  await enqueueHazardOp(store, {
    kind: operation === 'DELETE' ? 'reaction.remove' : 'reaction.add',
    entityId: reactionId,
    payload: {
      id: reactionId,
      user_id: reaction?.user_id ?? '',
      reaction_type_name: reaction?.reaction_type_name ?? '',
      content_type: contentType,
      content_id: contentId,
    },
  });
  const op = await findHazardOp(store, reactionId);
  if (op && attempts > 0) {
    await store.outbox.update(op.id, { attempts, nextAttemptAt: Date.now() });
  }
}

export async function noteCommentSynced(id: string, removed = false): Promise<void> {
  if (removed) {
    await commentTable.delete(id);
    return;
  }
  await commentTable.update(id, {
    last_synced: new Date().toISOString(),
    local_only: false,
  });
}

export async function noteReactionSynced(id: string, removed: boolean): Promise<void> {
  if (removed) {
    await reactionTable.delete(id);
    return;
  }
  await reactionTable.update(id, {
    last_synced: new Date().toISOString(),
    local_only: false,
    deleted: false,
  });
}

export async function getSyncMeta(key: string): Promise<string | undefined> {
  return readSyncMeta(key);
}

export async function setSyncMeta(key: string, value: string): Promise<void> {
  await writeSyncMeta(key, value);
}

export async function ensureUserStoreReady(userId: string): Promise<void> {
  const host = getEffectiveTenantHost() || 'localhost';
  const store = await openUserStore({
    host,
    userId,
    tenantId: readCachedTenantId(userId),
  });
  await adoptLegacyDatabase(store, userId, host);
  const { readDeviceMeta, deleteDeviceMeta } = await import('./deviceStore');
  if ((await readDeviceMeta('pendingCacheReset', host)) === '1') {
    await clearCache(store);
    await deleteDeviceMeta('pendingCacheReset', host);
  }
}
